import React, { FC, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import {
  Modal,
  Button,
  Paragraph,
  Svg,
  Flex,
  Card,
  Tags,
  Heading,
} from '@wunder-mobility/ui-components';
import { Subscription } from '@/features/subscriptions/types/Subscription.types';
import {
  useRequestCancelSubscriptionMutation,
  useRequestReactivateSubscriptionMutation,
  useRequestCancelSwitchMutation,
} from '@/features/subscriptions/services/Subscription.service';
import {
  getBillingCycleLabel,
  isSubscriptionExpiring,
  isSwitching,
} from '@/features/subscriptions/utils/subscriptionHelpers';
import { useRequestPaymentSourcesQuery } from '@/features/payment/services/Payment.service';
import { useRequestResolvedBranchQuery } from '@/features/branches/services/Branches.service';
import { format } from 'date-fns';
import styles from '@/features/subscriptions/assets/scss/ManageSubscriptionModal.module.scss';
import CurrencyDisplay from '@/core/components/CurrencyDisplay';
import { AutoRenewalStatus } from '@/features/subscriptions/enums';
import { DateFormats } from '@/core/enums';

interface ManageSubscriptionModalProps {
  subscription: Subscription;
  onChangePaymentMethod?: () => void;
  onCancel: () => void;
}

const ManageSubscriptionModal: FC<ManageSubscriptionModalProps> = ({
  subscription,
  onChangePaymentMethod,
  onCancel,
}) => {
  const { data: resolvedBranch } = useRequestResolvedBranchQuery();
  const { data: paymentSources } = useRequestPaymentSourcesQuery(
    { branchId: resolvedBranch?.id! },
    { skip: !resolvedBranch?.id },
  );

  const [cancelSubscription, { isLoading: isCancelling }] = useRequestCancelSubscriptionMutation();
  const [reactivateSubscription, { isLoading: isReactivating }] =
    useRequestReactivateSubscriptionMutation();
  const [cancelSwitch, { isLoading: isCancellingSwitch }] = useRequestCancelSwitchMutation();

  const cycleLabel = getBillingCycleLabel(subscription.interval);
  const defaultPaymentSource = paymentSources?.[0];
  const isActive = subscription.autoRenewal === AutoRenewalStatus.ACTIVE;
  const isExpiring = useMemo(() => isSubscriptionExpiring(subscription), [subscription]);
  // A pending switch is defined by state alone; the cancel-switch action must stay available
  // even if the switchingTarget payload is missing. The banner text is guarded separately.
  const hasPendingSwitch = isSwitching(subscription);
  const switchingTargetName =
    subscription.switchingTarget?.description || subscription.switchingTarget?.code;

  const handleCancel = async () => {
    try {
      await cancelSubscription({ subscriptionCode: subscription.code }).unwrap();
      onCancel?.();
    } catch (error) {
      return error;
    }
  };

  const handleCancelSwitch = async () => {
    if (subscription.id == null) {
      return;
    }
    try {
      await cancelSwitch({ id: subscription.id }).unwrap();
      onCancel?.();
    } catch (error) {
      return error;
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateSubscription({ subscriptionCode: subscription.code }).unwrap();
      onCancel?.();
    } catch (error) {
      return error;
    }
  };

  const handleChangePaymentMethod = () => {
    onCancel?.();
    onChangePaymentMethod?.();
  };

  const nextBillingDate = subscription.nextBillingDate
    ? format(new Date(subscription.nextBillingDate), DateFormats.SHORT_DATE)
    : null;

  const validUntilDate = subscription.validUntil
    ? format(new Date(subscription.validUntil), DateFormats.SHORT_DATE)
    : null;

  return (
    <Modal
      opened
      onClose={onCancel}
      variation="small"
      modalHeader={<Trans id="subscriptions.manage.title">Manage Subscription</Trans>}
      modalFooter={
        <Flex direction="column" gap="md" width="full">
          {hasPendingSwitch && (
            <>
              <Button
                variation="primary"
                onClick={() => {
                  handleCancelSwitch();
                }}
                loading={isCancellingSwitch}
                disabled={isCancellingSwitch}
                width="full"
                size="large"
              >
                <Trans id="subscriptions.manage.cancelSwitch">Cancel switch</Trans>
              </Button>
              <Paragraph size={5} textColor="text-400" marginBottom={0} align="center">
                <Trans id="subscriptions.manage.cancelSwitchDisclaimer">
                  Your current plan will continue and the pending switch will be removed.
                </Trans>
              </Paragraph>
            </>
          )}
          {isActive ? (
            <>
              <Button
                variation="secondary"
                onClick={() => {
                  handleCancel();
                }}
                loading={isCancelling}
                disabled={isCancelling}
                width="full"
                size="large"
              >
                <Trans id="subscriptions.manage.cancelSubscription">Cancel Subscription</Trans>
              </Button>
              <Paragraph size={5} textColor="text-400" marginBottom={0} align="center">
                <Trans id="subscriptions.manage.cancelDisclaimer">
                  You can cancel anytime. You'll still have access until your next billing date.
                </Trans>
              </Paragraph>
            </>
          ) : (
            <>
              <Button
                variation="primary"
                onClick={() => {
                  handleReactivate();
                }}
                loading={isReactivating}
                disabled={isReactivating}
                width="full"
                size="large"
              >
                <Trans id="subscriptions.manage.reactivate">Reactivate Subscription</Trans>
              </Button>
              <Paragraph size={5} textColor="text-400" marginBottom={0} align="center">
                <Trans id="subscriptions.manage.reactivateDisclaimer">
                  Your subscription will be reactivated and billing will resume.
                </Trans>
              </Paragraph>
            </>
          )}
        </Flex>
      }
    >
      <Flex direction="column" gap="lg">
        <Card className={styles['manage-subscription-modal__subscription']}>
          <Flex direction="column" gap="sm">
            {isActive && (
              <Flex>
                <Tags
                  bgColor="success"
                  size="small"
                  content={
                    <Trans id="subscriptions.status.active">{AutoRenewalStatus.ACTIVE}</Trans>
                  }
                  uppercase
                />
              </Flex>
            )}
            {isExpiring && (
              <Flex>
                <Tags
                  bgColor="error"
                  size="small"
                  content={<Trans id="subscriptions.status.expiring">EXPIRING</Trans>}
                  uppercase
                />
              </Flex>
            )}

            <Paragraph size={1} marginBottom={4}>
              {subscription.description || subscription.code}
            </Paragraph>

            <Flex align="flex-end">
              <Heading size={5}>
                <CurrencyDisplay value={subscription.price} currency={subscription.currency} />
              </Heading>
              <Paragraph size={4} textColor="text-400" marginBottom={12}>
                {cycleLabel}
              </Paragraph>
            </Flex>

            {isActive && validUntilDate && (
              <Paragraph size={4} marginBottom={0}>
                <Trans id="subscriptions.manage.nextBillingDate">
                  Next billing date: {validUntilDate}
                </Trans>
              </Paragraph>
            )}

            {hasPendingSwitch && switchingTargetName && (
              <Paragraph size={4} textColor="text-400" marginBottom={0}>
                {validUntilDate ? (
                  <Trans id="subscriptions.manage.pendingSwitchDated">
                    Switching to {switchingTargetName} on {validUntilDate}.
                  </Trans>
                ) : (
                  <Trans id="subscriptions.manage.pendingSwitch">
                    Switching to {switchingTargetName} at the next renewal.
                  </Trans>
                )}
              </Paragraph>
            )}

            {isExpiring && validUntilDate && (
              <Paragraph size={4} textColor="text-400" marginBottom={0}>
                <Trans id="subscriptions.manage.validUntil">Valid until: {validUntilDate}</Trans>
              </Paragraph>
            )}
          </Flex>
        </Card>

        <Flex direction="column" gap="sm">
          <Paragraph size={3} marginBottom={0}>
            <Trans id="subscriptions.manage.paymentMethod">Pay with saved card</Trans>
          </Paragraph>

          <Card className={styles['manage-subscription-modal__payment-method']}>
            <Flex align="center" justify="space-between">
              {defaultPaymentSource ? (
                <>
                  <Flex gap="sm" align="center">
                    <Svg
                      icon={(defaultPaymentSource.brand as any) || 'card'}
                      width={60}
                      height={35}
                    />
                    <Paragraph size={4} marginBottom={0}>
                      {defaultPaymentSource.brand || 'Card'} ending in {defaultPaymentSource.last4}
                    </Paragraph>
                  </Flex>
                  <Button variation="link" size="small" onClick={handleChangePaymentMethod}>
                    <Trans id="subscriptions.manage.changePayment">Change</Trans>
                  </Button>
                </>
              ) : (
                <Paragraph size={4} textColor="text-400" marginBottom={0}>
                  <Trans id="subscriptions.manage.noPaymentMethod">No payment method saved</Trans>
                </Paragraph>
              )}
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ManageSubscriptionModal;
